import { Injectable } from '@angular/core';

import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import {
  addDays,
  addMonths,
  addWeeks,
  differenceInHours,
  differenceInMinutes,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  isAfter,
  isBefore,
  isPast,
  max,
  min,
  parse,
  parseISO,
  set,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
  subWeeks,
  subYears,
} from 'date-fns';

type CalendarPeriod = 'day' | 'week' | 'month';

@Injectable({
  providedIn: 'root',
})
export class DateFormatService {
  private readonly defaultFormat = 'yyyy-MM-dd';
  private readonly defaultISOFormat = "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'";

  getMaxDate(year: number): Date {
    return subYears(new Date(), year);
  }

  structToDate(structDate: NgbDateStruct, formatString?: string): Date | string {
    if (!structDate) return new Date();

    const dateParsed = parse(
      `${structDate.year}-${structDate.month}-${structDate.day}`,
      this.defaultFormat,
      new Date()
    );

    return format(dateParsed, formatString || this.defaultFormat); // 'yyyy-MM-dd'
  }

  structToDateString(date: NgbDateStruct): string | null {
    if (!date) {
      return null;
    }

    const dateParsed = parse(`${date.year}-${date.month}-${date.day}`, this.defaultFormat, new Date());
    return format(dateParsed, this.defaultISOFormat);
  }

  dateToString(date: Date, hasISO?: boolean): string | null {
    if (!date) {
      return null;
    }

    return hasISO ? format(date, this.defaultISOFormat) : format(date, this.defaultFormat);
  }

  dateToStruct(date: Date): NgbDateStruct {
    const formatDate = format(date, this.defaultFormat);
    const [year, month, day] = formatDate.split('-').map(Number);
    return { year, month, day };
  }

  stringToStruct(date: string): NgbDateStruct {
    // 'yyyy-MM-dd'
    const [year, month, day] = date.split('T')[0].split('-').map(Number);
    return { year, month, day };
  }

  timeToDate(time: string): Date | null {
    if (!time) {
      return null;
    }

    const [hours, minutes] = time.split(':').map(Number);
    return set(new Date(), { hours, minutes, seconds: 0 });
  }

  timeToDateString(time: string): string | null {
    if (!time) {
      return null;
    }

    const [hours, minutes] = time.split(':').map(Number);
    const date = set(new Date(), { hours, minutes, seconds: 0 });
    return format(date, this.defaultISOFormat);
  }

  dateToTime(date: Date): string | null {
    if (!date) {
      return null;
    }

    return format(date, 'HH:mm');
  }

  getMidDate(date: Date): Date {
    return set(date, { hours: 12, minutes: 0, seconds: 0 });
  }

  addPeriod(period: CalendarPeriod, date: Date, amount: number): Date {
    return {
      day: addDays,
      week: addWeeks,
      month: addMonths,
    }[period](date, amount);
  }

  subPeriod(period: CalendarPeriod, date: Date, amount: number): Date {
    return {
      day: subDays,
      week: subWeeks,
      month: subMonths,
    }[period](date, amount);
  }

  startOfPeriod(period: CalendarPeriod, date: Date): Date {
    return {
      day: startOfDay,
      week: startOfWeek,
      month: startOfMonth,
    }[period](date);
  }

  endOfPeriod(period: CalendarPeriod, date: Date): Date {
    return {
      day: endOfDay,
      week: endOfWeek,
      month: endOfMonth,
    }[period](date);
  }

  isPast(date: string): boolean {
    const dateISO = parseISO(date);
    return isPast(dateISO);
  }

  isPastDate(date: Date): boolean {
    return isPast(date);
  }

  isAfter(date1: Date, date2: Date) {
    return isAfter(date1, date2);
  }

  isBefore(date1: Date, date2: Date) {
    return isBefore(date1, date2);
  }

  parseISO(date: string): Date {
    return parseISO(date);
  }

  differenceInHours(date: string): number {
    const dateISO = parseISO(date);
    return differenceInHours(dateISO, new Date());
  }

  differenceInMinutes(date: string): number {
    const dateISO = parseISO(date);
    return differenceInMinutes(dateISO, new Date());
  }

  min(dates: Date[]): Date {
    return min(dates);
  }

  max(dates: Date[]): Date {
    return max(dates);
  }

  addMonths(date: Date, amount: number): Date {
    return addMonths(date, amount);
  }
}
